import styled, { css as styledCSS } from "styled-components";

import { breakpoints, semiBoldAlias } from "Libs/theme";

export const IconContainer = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background-color: var(--mode-bg-neutral-weaker);
  width: 24px;
  height: 24px;
  cursor: pointer;
`;

export const LogContainer = styled.div`
  background-color: var(--mode-bg-neutral-weakest);
  padding: 8px 4px 0px;
  box-shadow: inset 0px 21px 5px -20px var(--mode-shadow-default);
  position: relative;

  & ${IconContainer} {
    visibility: hidden;
  }

  &:hover ${IconContainer} {
    visibility: visible;
  }
`;

export const DotDotDot = styled.div`
  font-family: var(--ff-mono), monospace;
  font-size: 12px;
  line-height: 20px;
  margin: 12px 0 0 58px;
  color: var(--mode-text-neutral-default);

  &:after {
    display: inline-block;
    content: "";
    animation: loader 2s infinite linear;
    width: 10px;
    margin-left: 2px;
    text-align: left;
  }

  @keyframes loader {
    0% {
      content: "";
    }
    25% {
      content: ".";
    }
    50% {
      content: "..";
    }
    75% {
      content: "...";
    }
  }
`;

export const Empty = styled.div`
  margin: 16px 0 0 58px;
  font-family: var(--ff-mono), monospace;
  font-size: 12px;
  line-height: 20px;
`;

export const LogPre = styled.pre<{ showLinesNumber?: boolean }>`
  height: 250px;
  position: relative;
  display: flex;
  flex: 1;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  background-color: var(--mode-bg-neutral-weakest);
  border-radius: var(
    --modal-code-modal-border-radius,
    var(--modal-0px-border-radius, var(--0px))
  );

  code {
    color: var(--mode-text-neutral-default);
    font-family: var(--ff-mono), monospace;
    font-weight: 400;
  }

  @media (max-width: ${breakpoints[1]}) {
    margin: 0 -16px;
  }

  ${props =>
    props.showLinesNumber &&
    styledCSS`
      &::before {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 42px;
        border-radius: var(--modal-code-modal-border-radius,var(--modal-0px-border-radius,var(--0px)));
        background-color: var(--mode-bg-neutral-weakest);
      }
    `};
`;

export const Container = styled.div`
  flex: 1;
  border-radius: 20px;
  > div {
    border-radius: 20px;

    padding: 0;
    box-sizing: border-box;
  }
`;

export const LineNumber = styled.div`
  display: inline-block;
  width: 34px;
  height: 100%;
  vertical-align: top;
  padding: 0 4px;
  cursor: pointer;
  background-color: var(
    --log-pre-modal-background-color,
    var(--modal-ebony-light-background-color, var(--ebony-light))
  );
  color: var(--mode-text-neutral-default);
  line-height: 21px;
  text-align: right;
  user-select: none;

  &:hover {
    color: var(--modal-snow-color, var(--snow));
  }
`;

export const TimeStamp = styled.div`
  display: none;
  position: absolute;
  right: 16px;
  top: 5px;
  color: var(--modal-gold-color, var(--gold));
  ${semiBoldAlias()};
  font-family: var(--ff-mono), monospace;
  font-size: 12px;
  line-height: 16px;
  user-select: none;
`;

export const Line = styled.div<{
  selected?: boolean;
  showTimeStamp?: boolean;
  $isError?: boolean;
}>`
  position: relative;
  background-color: ${props =>
    props.$isError ? "var(--mode-bg-critical-weakest)" : "none"};

  ${LineNumber} {
    background-color: ${props =>
      props.$isError ? "var(--mode-bg-critical-weakest)" : "inherit"};
  }

  &::selection,
  *::selection {
    background-color: var(
      --modal-color-primary-100-background-color,
      var(--color-primary-100)
    );
  }

  code {
    padding: 0;
    margin: 0 0 0 16px;
    max-width: calc(100% - 150px);
    line-height: 21px;
    background: none;
    white-space: break-spaces;
    display: inline-block;
    width: 100%;
  }

  &:hover {
    background-color: ${props =>
      props.$isError
        ? "var(--mode-bg-critical-weakest)"
        : "var(--modal-light-blue-hover-background-color,var(--light-blue-hover))"};

    ${LineNumber} {
      background-color: ${props =>
        props.$isError
          ? "var(--mode-bg-critical-weakest)"
          : "var(--modal-light-blue-hover-background-color,var(--light-blue-hover))"};
      color: var(--modal-snow-color, var(--snow));
    }
    ${TimeStamp} {
      display: block;
    }
  }

  ${props =>
    props.selected &&
    styledCSS`
      code {
        background: none;
      }
      &,
      ${LineNumber}{
        background-color: var(--modal-light-blue-hover-background-color,var(--light-blue-hover));
        color: var(--modal-snow-color,var(--snow));
      }
    `};

  ${props =>
    props.showTimeStamp &&
    styledCSS`
      ${TimeStamp} {
        display: block;
      }
    `};
`;

export const Spacer = styled.div`
  height: 16px !important;
`;
